import { injectHistory } from '@monofe/app-common';
import type { IZoomPanPinchRef } from '@monofe/ui-common';
import { HighlightableButton, IconMinus, IconPlus, PageControl } from '@monofe/ui-common';
import { BackLink } from '@monofe/ui-common/nav';
import FadeTransition from '@monofe/ui-common/transition/FadeTransition';
import type { CSSProperties, RefObject } from 'react';
import { createRef, useEffect, useState } from 'react';
import ItemView from './ItemView';
import { StyleElement, classNameForElement, styleForElement } from './MainView.style';
import type {
    IFloorplanIndexProps as IFloorplanIndexProperties,
    IFloorplanItemViewModel,
} from './interface';

const _referencesFromItems = (itemsIn: IFloorplanItemViewModel[]) =>
    itemsIn.map(() => createRef<IZoomPanPinchRef>());

function useItemReferences(items: IFloorplanItemViewModel[]): RefObject<IZoomPanPinchRef>[] {
    const [references, setReferences] = useState<RefObject<IZoomPanPinchRef>[]>(
        _referencesFromItems(items),
    );

    useEffect(() => {
        setReferences(_referencesFromItems(items));
    }, [setReferences, items]);

    return references;
}

function MainView(properties: IFloorplanIndexProperties): JSX.Element {
    const history = injectHistory();

    const style = (element: StyleElement, overrides?: CSSProperties) =>
        styleForElement(element, properties.model, overrides);
    const className = (element: StyleElement) => classNameForElement(element, properties.model);

    const [pageIndex, setPageIndex] = useState<number>(0);
    const itemReferences = useItemReferences(properties.model.items);

    const handleZoomIn = () => itemReferences[pageIndex]?.current?.zoomIn();

    const handleZoomOut = () => itemReferences[pageIndex]?.current?.zoomOut();

    const handlePreviousPage = () => {
        setPageIndex(index => Math.max(index - 1, 0));
    };

    const handleNextPage = () => {
        setPageIndex(index => Math.min(index + 1, properties.model.items.length - 1));
    };

    const ItemViews = properties.model.items.map((item, index) => {
        const itemReference = itemReferences[index];
        if (itemReference === undefined)
            throw new Error(`No ref object for item with index ${index}`);
        return (
            <ItemView
                className={className(StyleElement.Item)}
                style={style(StyleElement.Item)}
                key={item.imageUrls.at(0)?.src ?? index}
                imageRef={itemReference}
                {...item}
            />
        );
    });

    return (
        <FadeTransition
            className={className(StyleElement.Container)}
            style={style(StyleElement.Container)}
        >
            <BackLink
                {...properties.model.backButton}
                href={history.previousLocation()?.pathname ?? ''}
                onClick={properties.model.onBack}
            />
            <div
                className={className(StyleElement.ItemContainer)}
                style={style(StyleElement.ItemContainer, { left: `-${pageIndex * 100}vw` })}
            >
                {ItemViews}
            </div>
            {properties.model.items.length > 1 && (
                <PageControl
                    className={className(StyleElement.PageControl)}
                    style={style(StyleElement.PageControl)}
                    activeIndex={pageIndex}
                    totalPages={properties.model.items.length}
                    backgroundColor={properties.model.zoomControlsBackgroundColor}
                    tintColor={properties.model.zoomControlsTintColor}
                    onLeftClick={handlePreviousPage}
                    onRightClick={handleNextPage}
                    onIndexClick={setPageIndex}
                />
            )}
            <HighlightableButton
                hideOnAccessibilityTree
                onClick={handleZoomIn}
                className={className(StyleElement.ButtonPlus)}
                style={style(StyleElement.ButtonPlus)}
            >
                <IconPlus
                    className={className(StyleElement.IconPlus)}
                    style={style(StyleElement.IconPlus)}
                />
            </HighlightableButton>
            <HighlightableButton
                hideOnAccessibilityTree
                onClick={handleZoomOut}
                className={className(StyleElement.ButtonMinus)}
                style={style(StyleElement.ButtonMinus)}
            >
                <IconMinus
                    className={className(StyleElement.IconMinus)}
                    style={style(StyleElement.IconMinus)}
                />
            </HighlightableButton>
        </FadeTransition>
    );
}

export default MainView;
